import React, { FC } from "react";
import { darkTheme } from "@yleisradio/yds-core";
import styled from "styled-components";

import { UI_SIZE } from "../../enums";
import i18n from "../../service/i18n";
import { focusColor, shareOverlayEmbedCodeColor } from "../../styles/variables";

interface ResponsiveProps {
  uiSize: UI_SIZE;
}

interface HideableProps {
  isVisible: boolean;
}

type ShareEmbedCodeDivProps = ResponsiveProps & HideableProps;

const ShareEmbedCodeDiv = styled.div<ShareEmbedCodeDivProps>`
  box-sizing: border-box;
  display: ${props => (props.isVisible ? "block" : "none")};
  margin-bottom: 2px;
  margin-top: 9px;
  position: relative;
  width: ${(props: ShareEmbedCodeDivProps) =>
    props.uiSize === UI_SIZE.SMALL || props.uiSize === UI_SIZE.XSMALL
      ? "90%"
      : "30em"};

  &::selection {
    background: ${shareOverlayEmbedCodeColor};
  }
`;

const ShareEmbedInput = styled.input<ResponsiveProps>`
  background: content-box transparent;
  border: 1px solid ${darkTheme.BORDER};
  box-sizing: border-box;
  color: white;
  font-size: 14px;
  padding: 4px;
  width: 100%;
  ${props => {
    if (props.uiSize === UI_SIZE.DEFAULT) {
      return "padding: 11px;";
    }
  }}

  &:focus-visible {
    border-color: ${focusColor};
    outline: none;
  }
`;

const ShareTooltip = styled.div<HideableProps>`
  display: ${props => (props.isVisible ? "block" : "none")};
  left: 50%;
  position: absolute;
  top: -23px;
  transform: translate(-50%);
  white-space: nowrap;
`;

const ShareTooltipLabel = styled.span`
  border-radius: 6px;
  border: 1px solid ${darkTheme.BORDER};
  font-size: 10px;
  padding: 3px;

  &:after {
    border: 5px solid;
    border-color: ${darkTheme.BORDER} transparent transparent;
    content: "";
    left: 50%;
    margin-left: -5px;
    position: absolute;
    top: 24px;
  }
`;

interface Props {
  addSeek: boolean;
  areenaLink: string;
  embedCodeAreaVisible: boolean;
  embedCodeVisible: boolean;
  id: string;
  language: string;
  setEmbedCode: (code) => void;
  shareFromInSeconds: number;
  shareTooltipVisible: boolean;
  uiSize: UI_SIZE;
}

const seekString = (progressInSeconds: number) => {
  if (Number.isInteger(progressInSeconds)) {
    return ` data-seek="${Math.round(progressInSeconds)}"`;
  } else {
    return "";
  }
};

const getEmbedCode = ({ addSeek, id, language, progressInSeconds }) => {
  return `<div class="yle_areena_player" data-id="${id}"${
    addSeek ? seekString(progressInSeconds) : ""
  }${
    language && language !== "fin" ? ` data-locale="${language}"` : ""
  }></div><script src="${__BASE_URL}/embed.js" defer></script>`;
};

const ShareEmbedCode: FC<Props> = ({
  addSeek,
  areenaLink,
  embedCodeAreaVisible,
  embedCodeVisible,
  id,
  language,
  setEmbedCode,
  shareFromInSeconds,
  shareTooltipVisible,
  uiSize
}) => (
  <ShareEmbedCodeDiv isVisible={embedCodeAreaVisible} uiSize={uiSize}>
    <ShareTooltip isVisible={shareTooltipVisible}>
      <ShareTooltipLabel>
        {i18n("kopioitu_leikepoydalle", { language })}
      </ShareTooltipLabel>
    </ShareTooltip>
    <ShareEmbedInput
      data-testid="embedcode"
      type="text"
      value={
        embedCodeVisible
          ? getEmbedCode({
              addSeek,
              id,
              language,
              progressInSeconds: shareFromInSeconds
            })
          : areenaLink
      }
      ref={code => setEmbedCode(code)}
      readOnly
      uiSize={uiSize}
    />
  </ShareEmbedCodeDiv>
);

export default ShareEmbedCode;
